import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Switch,
  Alert,
  Select,
  Radio,
  Button,
  message,
  Divider,
  Space,
  Input,
  Checkbox,
  Table,
  Spin,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from '@ant-design/icons';

const { Option } = Select;

class ModmedQueuedFaxSettings extends Component {
  constructor(props) {
    super(props);
    this.state = {
      enabled: false,
      mode: 'all',
      uploadToChart: true,
      finalNotesOnly: false,
      faxNumberId: null,
      rules: [],
      saving: false,
    };
  }

  componentDidMount() {
    this.loadSettings();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.settings !== this.props.settings) {
      this.loadSettings();
    }
  }

  createClientId = () => `${Date.now()}_${Math.random().toString(16).slice(2)}`;

  ensureClientIds = (rules = []) => rules.map((rule) => ({
    ...rule,
    clientId: rule.clientId || this.createClientId(),
  }));

  optionText = (option) => {
    const children = option?.children ?? option?.props?.children;

    if (Array.isArray(children)) {
      return children.filter(Boolean).join('');
    }

    if (children === null || children === undefined) {
      return '';
    }

    return String(children);
  };

  filterOptionByChildren = (input, option) => {
    const text = this.optionText(option).toLowerCase();
    const search = String(input || '').toLowerCase();
    return text.includes(search);
  };

  getEmrAuthErrorMessage = () => {
    const { templatesError, facilitiesError } = this.props;
    const errors = [templatesError, facilitiesError].filter(Boolean);
    let matchedMessage = null;
    errors.some((error) => {
      let message;
      try {
        message = String(error);
      } catch (stringifyError) {
        return false;
      }
      if (message.includes('Authentication failed') || message.includes('EMA credentials not configured')) {
        matchedMessage = message;
        return true;
      }
      return false;
    });
    return matchedMessage;
  };

  loadSettings = () => {
    const { settings, autoEnable } = this.props;
    if (settings && Object.keys(settings).length > 0) {
      // Handle enabled as either boolean or string from backend
      const isEnabled = settings.enabled === true || settings.enabled === 'true';
      this.setState({
        enabled: isEnabled || autoEnable || false,
        mode: settings.mode || 'all',
        uploadToChart: settings.upload_to_chart !== false && settings.upload_to_chart !== 'false',
        finalNotesOnly: settings.final_notes_only === true || settings.final_notes_only === 'true',
        faxNumberId: settings.fax_number_id || null,
        rules: this.ensureClientIds(settings.rules || []),
      });
    } else if (autoEnable) {
      this.setState({ enabled: true });
    }
  };

  rulesForSave = () => {
    const { rules } = this.state;
    return rules.map((rule) => {
      const { clientId, ...rest } = rule;
      return rest;
    });
  };

  handleSave = async () => {
    const {
      enabled,
      mode,
      uploadToChart,
      finalNotesOnly,
      faxNumberId,
      rules,
    } = this.state;
    const { updateSettings } = this.props;

    if (enabled && mode === 'all' && !faxNumberId) {
      message.error('Please select a fax number');
      return;
    }

    if (enabled && mode === 'rules' && rules.length === 0) {
      message.error('Please add at least one rule');
      return;
    }

    // Validate each rule has a name and fax number
    if (enabled && mode === 'rules') {
      for (let i = 0; i < rules.length; i += 1) {
        if (!rules[i].name || rules[i].name.trim() === '') {
          message.error(`Rule ${i + 1}: Please enter a name`);
          return;
        }
        if (!rules[i].fax_number_id) {
          message.error(`Rule ${i + 1}: Please select a fax number`);
          return;
        }
      }
    }

    this.setState({ saving: true });

    try {
      await updateSettings({
        enabled,
        mode,
        upload_to_chart: mode === 'all' ? uploadToChart : null,
        final_notes_only: mode === 'all' ? finalNotesOnly : null,
        fax_number_id: mode === 'all' ? faxNumberId : null,
        rules: mode === 'rules' ? this.rulesForSave() : [],
      });
      message.success('Settings saved successfully');
    } catch (error) {
      // Show backend error message if available
      const errorMsg = error?.response?.data?.error || 'Failed to save settings';
      message.error(errorMsg);
    } finally {
      this.setState({ saving: false });
    }
  };

  isFormValid = () => {
    const {
      enabled,
      mode,
      faxNumberId,
      rules,
    } = this.state;
    const emrAuthError = this.getEmrAuthErrorMessage();

    // If disabled, form is always valid (can save disabled state)
    if (!enabled) {
      return true;
    }

    if (emrAuthError) {
      return false;
    }

    if (mode === 'all') {
      // All mode requires a fax number
      return !!faxNumberId;
    }

    if (mode === 'rules') {
      // Rules mode requires at least one rule, each with a name and fax number
      if (rules.length === 0) {
        return false;
      }
      return rules.every((rule) => !!rule.name && rule.name.trim() !== '' && !!rule.fax_number_id);
    }

    return true;
  };

  addRule = () => {
    const { rules } = this.state;
    this.setState({
      rules: [
        ...rules,
        {
          clientId: this.createClientId(),
          name: '',
          template_ids: [],
          facility_ids: [],
          final_notes_only: false,
          upload_to_chart: true,
          fax_number_id: null,
        },
      ],
    });
  };

  updateRule = (index, field, value) => {
    const { rules } = this.state;
    const newRules = [...rules];
    newRules[index] = { ...newRules[index], [field]: value };
    this.setState({ rules: newRules });
  };

  removeRule = (index) => {
    const { rules } = this.state;
    this.setState({
      rules: rules.filter((_, i) => i !== index),
    });
  };

  moveRule = (fromIndex, toIndex) => {
    const { rules } = this.state;
    if (toIndex < 0 || toIndex >= rules.length) return;

    const newRules = [...rules];
    const [moved] = newRules.splice(fromIndex, 1);
    newRules.splice(toIndex, 0, moved);
    this.setState({ rules: newRules });
  };

  renderAllModeSettings = () => {
    const { faxNumberId, finalNotesOnly } = this.state;
    const { faxNumbers } = this.props;

    return (
      <div style={{ marginTop: 16 }}>
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 8 }}>
            <strong>Fax Number:</strong>
          </div>
          <Select
            value={faxNumberId ?? undefined}
            onChange={(value) => this.setState({ faxNumberId: value })}
            placeholder="Select a fax number"
            style={{ width: 300 }}
          >
            {faxNumbers.map((faxEntry) => {
              const [number, name, , , faxId] = faxEntry || [];
              const faxNumberIdOption = faxId || number;
              if (!faxNumberIdOption) return null;

              return (
                <Option key={faxNumberIdOption} value={faxNumberIdOption}>
                  {number}
                  {' - '}
                  {name}
                </Option>
              );
            })}
          </Select>
        </div>
        <div>
          <Checkbox
            checked={finalNotesOnly}
            onChange={(e) => this.setState({ finalNotesOnly: e.target.checked })}
          >
            Final notes only
          </Checkbox>
        </div>
      </div>
    );
  };

  renderRulesModeSettings = () => {
    const { rules } = this.state;
    const {
      faxNumbers,
      templates,
      facilities,
      templatesError,
      facilitiesError,
    } = this.props;

    const columns = [
      {
        title: 'Order',
        key: 'order',
        width: 90,
        align: 'center',
        render: (_value, _record, index) => (
          <Space size={4}>
            <Button
              type="text"
              icon={<ArrowUpOutlined />}
              onClick={() => this.moveRule(index, index - 1)}
              disabled={index === 0}
              size="small"
            />
            <Button
              type="text"
              icon={<ArrowDownOutlined />}
              onClick={() => this.moveRule(index, index + 1)}
              disabled={index === rules.length - 1}
              size="small"
            />
            <span style={{ width: 18, display: 'inline-block', textAlign: 'right' }}>
              {index + 1}
            </span>
          </Space>
        ),
      },
      {
        title: 'Rule Name',
        dataIndex: 'name',
        key: 'name',
        width: 150,
        render: (value, _record, index) => (
          <Input
            value={value}
            onChange={(e) => this.updateRule(index, 'name', e.target.value)}
            placeholder="e.g., Cardiology"
            size="small"
          />
        ),
      },
      {
        title: 'Templates',
        dataIndex: 'template_ids',
        key: 'template_ids',
        width: 200,
        render: (value, _record, index) => (
          <Select
            mode="multiple"
            value={value}
            onChange={(val) => this.updateRule(index, 'template_ids', val)}
            placeholder={templatesError || 'All'}
            size="small"
            style={{ width: '100%' }}
            maxTagCount={1}
            disabled={!!templatesError}
            status={templatesError ? 'warning' : undefined}
            filterOption={this.filterOptionByChildren}
          >
            {templates.map((t) => (
              <Option key={t.id} value={t.id}>
                {t.name || '(Unnamed template)'}
              </Option>
            ))}
          </Select>
        ),
      },
      {
        title: 'Facilities',
        dataIndex: 'facility_ids',
        key: 'facility_ids',
        width: 200,
        render: (value, _record, index) => (
          <Select
            mode="multiple"
            value={value}
            onChange={(val) => this.updateRule(index, 'facility_ids', val)}
            placeholder={facilitiesError || 'All'}
            size="small"
            style={{ width: '100%' }}
            maxTagCount={1}
            disabled={!!facilitiesError}
            status={facilitiesError ? 'warning' : undefined}
            filterOption={this.filterOptionByChildren}
          >
            {facilities.map((f) => (
              <Option key={f.id} value={f.id}>
                {f.name || '(Unnamed facility)'}
              </Option>
            ))}
          </Select>
        ),
      },
      {
        title: 'Final Only',
        dataIndex: 'final_notes_only',
        key: 'final_notes_only',
        width: 80,
        align: 'center',
        render: (value, _record, index) => (
          <Checkbox
            checked={value}
            onChange={(e) => this.updateRule(index, 'final_notes_only', e.target.checked)}
          />
        ),
      },
      {
        title: 'Upload',
        dataIndex: 'upload_to_chart',
        key: 'upload_to_chart',
        width: 70,
        align: 'center',
        render: (value, _record, index) => (
          <Checkbox
            checked={value !== false}
            onChange={(e) => this.updateRule(index, 'upload_to_chart', e.target.checked)}
          />
        ),
      },
      {
        title: 'Fax Number',
        dataIndex: 'fax_number_id',
        key: 'fax_number_id',
        width: 180,
        render: (value, _record, index) => (
          <Select
            value={value ?? undefined}
            onChange={(val) => this.updateRule(index, 'fax_number_id', val)}
            placeholder="Select"
            size="small"
            style={{ width: '100%' }}
          >
            {faxNumbers.map((faxEntry) => {
              const [number, name, , , faxId] = faxEntry || [];
              const faxNumberIdOption = faxId || number;
              if (!faxNumberIdOption) return null;

              return (
                <Option key={faxNumberIdOption} value={faxNumberIdOption}>
                  {number}
                  {' - '}
                  {name}
                </Option>
              );
            })}
          </Select>
        ),
      },
      {
        title: '',
        key: 'actions',
        width: 50,
        render: (_value, _record, index) => (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => this.removeRule(index)}
            size="small"
          />
        ),
      },
    ];

    return (
      <div style={{ marginTop: 16 }}>
        <Table
          dataSource={rules}
          columns={columns}
          rowKey={(record) => record.clientId}
          pagination={false}
          size="small"
          bordered
          tableLayout="fixed"
        />
        <Button
          type="dashed"
          onClick={this.addRule}
          icon={<PlusOutlined />}
          style={{ marginTop: 12 }}
        >
          Add Rule
        </Button>
      </div>
    );
  };

  render() {
    const {
      enabled,
      mode,
      uploadToChart,
      saving,
    } = this.state;
    const { templatesLoading, facilitiesLoading } = this.props;
    const isLoadingRulesData = (templatesLoading || facilitiesLoading) && mode === 'rules';
    const emrAuthError = this.getEmrAuthErrorMessage();
    const isEmrAuthBlocked = enabled && !!emrAuthError;

    return (
      <div>
        <div style={{ marginBottom: 16 }}>
          <Space>
            <span>Enable:</span>
            <Switch
              checked={enabled}
              onChange={(checked) => this.setState({ enabled: checked })}
            />
          </Space>
        </div>

        {enabled && (
          <>
            <Divider />

            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 8 }}>
                <strong>Mode:</strong>
              </div>
              <Radio.Group
                value={mode}
                onChange={(e) => this.setState({ mode: e.target.value })}
              >
                <Radio value="all">Send all pending faxes</Radio>
                <Radio value="rules">Use rules to filter</Radio>
              </Radio.Group>
            </div>

            {mode === 'all' && (
              <div style={{ marginBottom: 16 }}>
                <Checkbox
                  checked={uploadToChart}
                  onChange={(e) => this.setState({ uploadToChart: e.target.checked })}
                >
                  Upload to patient chart after sending
                </Checkbox>
              </div>
            )}

            {mode === 'all' && this.renderAllModeSettings()}
            {mode === 'rules' && isLoadingRulesData && (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <Spin size="large" />
                <div style={{ marginTop: 16 }}>Loading templates and facilities...</div>
              </div>
            )}
            {mode === 'rules' && !isLoadingRulesData && this.renderRulesModeSettings()}
          </>
        )}

        <Divider />

        {isEmrAuthBlocked && (
          <Alert
            type="warning"
            showIcon
            message="Unable to save settings while EMA authentication is failing."
            description={emrAuthError}
            style={{ marginBottom: 12 }}
          />
        )}

        <Button
          type="primary"
          onClick={this.handleSave}
          loading={saving}
          disabled={!this.isFormValid()}
        >
          Save Settings
        </Button>
      </div>
    );
  }
}

ModmedQueuedFaxSettings.propTypes = {
  settings: PropTypes.object,
  faxNumbers: PropTypes.array,
  templates: PropTypes.array,
  facilities: PropTypes.array,
  templatesLoading: PropTypes.bool,
  facilitiesLoading: PropTypes.bool,
  templatesError: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  facilitiesError: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  updateSettings: PropTypes.func.isRequired,
  autoEnable: PropTypes.bool,
};

ModmedQueuedFaxSettings.defaultProps = {
  settings: {},
  faxNumbers: [],
  templates: [],
  facilities: [],
  templatesLoading: false,
  facilitiesLoading: false,
  templatesError: null,
  facilitiesError: null,
  autoEnable: false,
};

export default ModmedQueuedFaxSettings;
